// Pasul 54.5: butonul „Apreciază” — acelasi stil ca „Distribuie” (MUI outlined, small), grupat cu el.
// Contorul vine din proiectia publica (`likes`); starea „apreciat de mine” vine din useAprecieriMele.
// La click starea se schimba imediat si revine daca serverul refuza.
import React, {useEffect, useState} from 'react';
import {Box, Button, IconButton, Snackbar, Typography} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import {schimbaApreciere, textAprecieri} from '../utils/aprecieri';
import DistribuieButton, {sxIconCompact} from './DistribuieButton';

export const MESAJ_PREA_MULTE = 'Prea multe aprecieri într-un minut. Reîncearcă puțin mai târziu.';
export const MESAJ_EROARE = 'Aprecierea nu a putut fi salvată.';

// `compact` — pe cardurile de lucrari din grila expozitiei / membrului: doar inima (cu numarul alaturi),
// ca sa nu concureze vizual cu lucrarea.
export default function LikeButton({tip, id, likes = 0, apreciat, nume, compact = false}) {
    const [stare, setStare] = useState({likes: Number(likes) || 0, likedByMe: !!apreciat});
    const [ocupat, setOcupat] = useState(false);
    const [mesaj, setMesaj] = useState('');

    // Proiectia sau starea „ale mele” pot sosi dupa randare (ex. dupa schimbarea paginii).
    useEffect(() => {
        setStare((s) => ({likes: Number(likes) || 0, likedByMe: apreciat ?? s.likedByMe}));
    }, [likes, apreciat]);

    // 54.5b: `likes === null` — lucrare teaser (nedezvaluita), fara aprecieri; fara id — API vechi.
    if (!id || likes === null) return null;

    const comuta = async () => {
        if (ocupat) return;
        const anterior = stare;
        const nou = !anterior.likedByMe;
        setStare({likes: Math.max(0, anterior.likes + (nou ? 1 : -1)), likedByMe: nou});
        setOcupat(true);
        try {
            const data = await schimbaApreciere(tip, id, nou);
            setStare({likes: Number(data?.likes) || 0, likedByMe: !!data?.likedByMe});
        } catch (e) {
            setStare(anterior);
            setMesaj(e?.response?.status === 429 ? MESAJ_PREA_MULTE : MESAJ_EROARE);
        } finally {
            setOcupat(false);
        }
    };

    const actiune = `${stare.likedByMe ? 'Retrage aprecierea' : 'Apreciază'}${nume ? `: ${nume}` : ''}`;
    const eticheta = stare.likes > 0 ? `${actiune} (${textAprecieri(stare.likes)})` : actiune;
    const icon = stare.likedByMe ? <FavoriteIcon fontSize={compact ? 'small' : undefined} /> : <FavoriteBorderIcon fontSize={compact ? 'small' : undefined} />;
    const snackbar = <Snackbar open={!!mesaj} autoHideDuration={4000} onClose={() => setMesaj('')} message={mesaj}
                               anchorOrigin={{vertical: 'bottom', horizontal: 'center'}} />;
    if (compact) {
        return <Box sx={{display: 'inline-flex', alignItems: 'center', minWidth: 0}}>
            <IconButton size="small" onClick={comuta} aria-pressed={stare.likedByMe} aria-label={eticheta} title={eticheta}
                        sx={sxIconCompact(stare.likedByMe)}>
                {icon}
            </IconButton>
            {stare.likes > 0 && <Typography variant="caption" color="text.secondary" aria-hidden="true"
                                            sx={{fontSize: 11, lineHeight: 1, opacity: .8}}>{stare.likes}</Typography>}
            {snackbar}
        </Box>;
    }
    return <>
        <Button size="small" variant="outlined" onClick={comuta} aria-pressed={stare.likedByMe} aria-label={eticheta}
                title={eticheta}
                startIcon={icon} sx={{flexShrink: 0}}>
            {textAprecieri(stare.likes)}
        </Button>
        {snackbar}
    </>;
}

// Gruparea „Apreciază” + „Distribuie” pe carduri: Like în stânga, Distribuie în dreapta;
// pe carduri înguste trec unul sub altul (flex-wrap), fără să iasă din card.
export const ActiuniContinut = ({children, sx}) => (
    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between', alignItems: 'center', minWidth: 0, ...sx}}>
        {children}
    </Box>
);

// Actiunile unui card: aprecierea elementului si distribuirea adresei lui publice (`cale`).
export const ActiuniCard = ({tip, item, nume, cale, text, apreciat, sx, compact = false}) => (
    <ActiuniContinut sx={sx}>
        {/* Fara apreciere (teaser) ramane un loc gol, ca „Distribuie” sa stea tot in dreapta. */}
        {item?.likes === null ? <span /> : <LikeButton tip={tip} id={item?.id} likes={item?.likes} apreciat={apreciat} nume={nume} compact={compact} />}
        <DistribuieButton cale={cale} titlu={`${nume} — ArtaNFT`} text={text || nume} compact={compact} />
    </ActiuniContinut>
);
